import React, { useState, useEffect, useCallback } from 'react';
import { getRescueActions, createRescueAction, updateRescueAction, deleteRescueAction } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Pencil, Loader2, GripVertical, Trash2 } from 'lucide-react';

const ACTION_TYPES = ['exercise', 'diet', 'lifestyle'];

const emptyAction = {
  title: '',
  icon: '',
  description: '',
  actionType: 'exercise',
  estimatedReduction: '',
  timing: '',
  steps: [],
  evidenceNote: '',
  displayOrder: 1,
  isActive: true,
};

const RescueActionsPage = () => {
  const [actions, setActions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [formData, setFormData] = useState(emptyAction);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [stepInput, setStepInput] = useState('');

  const fetchActions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getRescueActions();
      setActions(response.data);
    } catch (error) {
      toast.error('Failed to load rescue actions');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  const openNewPanel = () => {
    setEditingAction(null);
    const maxOrder = Math.max(0, ...actions.map(a => a.displayOrder || 0));
    setFormData({ ...emptyAction, displayOrder: maxOrder + 1 });
    setErrors({});
    setPanelOpen(true);
  };

  const openEditPanel = (action) => {
    setEditingAction(action);
    setFormData({ ...action, steps: action.steps || [] });
    setErrors({});
    setPanelOpen(true);
  };

  const addStep = () => {
    if (stepInput.trim()) {
      setFormData({ ...formData, steps: [...formData.steps, stepInput.trim()] });
      setStepInput('');
    }
  };

  const removeStep = (index) => {
    setFormData({ ...formData, steps: formData.steps.filter((_, i) => i !== index) });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = true;
    if (!formData.icon.trim()) newErrors.icon = true;
    if (!formData.description.trim()) newErrors.description = true;
    if (!formData.estimatedReduction.trim()) newErrors.estimatedReduction = true;
    if (!formData.timing.trim()) newErrors.timing = true;
    if (!formData.evidenceNote.trim()) newErrors.evidenceNote = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      if (editingAction) {
        await updateRescueAction(editingAction.id, formData);
        toast.success('Rescue action updated successfully');
      } else {
        await createRescueAction(formData);
        toast.success('Rescue action created successfully');
      }
      setPanelOpen(false);
      fetchActions();
    } catch (error) {
      toast.error('Error saving — try again');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingAction) return;
    setIsSaving(true);
    try {
      await deleteRescueAction(editingAction.id);
      toast.success('Rescue action deleted successfully');
      setPanelOpen(false);
      setDeleteDialogOpen(false);
      fetchActions();
    } catch (error) {
      toast.error('Error deleting — try again');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="rescue-actions-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Rescue Actions</h1>
        <Button onClick={openNewPanel} className="bg-[#1A6B3C] hover:bg-[#155a32]" data-testid="new-action-btn">
          <Plus size={16} className="mr-1" /> New Action
        </Button>
      </div>

      <div className="bg-white rounded-lg border overflow-hidden">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : actions.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500 mb-4">No rescue actions found</p>
            <Button onClick={openNewPanel} className="bg-[#1A6B3C] hover:bg-[#155a32]">
              <Plus size={16} className="mr-1" /> Add First Action
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-12">Icon</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Reduction</TableHead>
                <TableHead className="text-center">Order</TableHead>
                <TableHead className="text-center">Active</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {actions.map((action) => (
                <TableRow key={action.id} data-testid={`action-row-${action.id}`}>
                  <TableCell className="text-xl">{action.icon}</TableCell>
                  <TableCell className="font-medium">{action.title}</TableCell>
                  <TableCell>
                    <span className="capitalize text-sm bg-gray-100 px-2 py-0.5 rounded">{action.actionType}</span>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">{action.estimatedReduction}</TableCell>
                  <TableCell className="text-center">{action.displayOrder}</TableCell>
                  <TableCell className="text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${action.isActive ? 'bg-green-500' : 'bg-gray-300'}`} />
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => openEditPanel(action)}>
                      <Pencil size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent className="slide-panel-520 overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingAction ? 'Edit Rescue Action' : 'New Rescue Action'}</SheetTitle>
            <SheetDescription>
              {editingAction ? 'Modify rescue action details' : 'Create a new blood sugar rescue action'}
            </SheetDescription>
          </SheetHeader>
          
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label className={errors.title ? 'text-red-500' : ''}>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className={errors.title ? 'border-red-500' : ''}
                />
              </div>
              <div>
                <Label className={errors.icon ? 'text-red-500' : ''}>Icon (emoji) *</Label>
                <Input
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="🚶"
                  className={errors.icon ? 'border-red-500' : ''}
                />
              </div>
            </div>

            <div>
              <Label className={errors.description ? 'text-red-500' : ''}>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className={errors.description ? 'border-red-500' : ''}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Action Type *</Label>
                <Select value={formData.actionType} onValueChange={(v) => setFormData({ ...formData, actionType: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTION_TYPES.map(type => (
                      <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={errors.estimatedReduction ? 'text-red-500' : ''}>Estimated Reduction *</Label>
                <Input
                  value={formData.estimatedReduction}
                  onChange={(e) => setFormData({ ...formData, estimatedReduction: e.target.value })}
                  placeholder="15-30 mg/dL"
                  className={errors.estimatedReduction ? 'border-red-500' : ''}
                />
              </div>
            </div>

            <div>
              <Label className={errors.timing ? 'text-red-500' : ''}>Timing *</Label>
              <Input
                value={formData.timing}
                onChange={(e) => setFormData({ ...formData, timing: e.target.value })}
                placeholder="Within 30 minutes after eating"
                className={errors.timing ? 'border-red-500' : ''}
              />
            </div>

            {/* Steps */}
            <div>
              <Label>Steps</Label>
              <div className="space-y-2 mt-2">
                {formData.steps.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-2 bg-gray-50 p-2 rounded">
                    <GripVertical size={14} className="text-gray-400 mt-0.5" />
                    <span className="flex-1 text-sm">{step}</span>
                    <button onClick={() => removeStep(idx)} className="text-gray-400 hover:text-red-500">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input
                    value={stepInput}
                    onChange={(e) => setStepInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addStep())}
                    placeholder="Add step..."
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" size="icon" onClick={addStep}>
                    <Plus size={16} />
                  </Button>
                </div>
              </div>
            </div>

            <div>
              <Label className={errors.evidenceNote ? 'text-red-500' : ''}>Evidence Note *</Label>
              <Textarea
                value={formData.evidenceNote}
                onChange={(e) => setFormData({ ...formData, evidenceNote: e.target.value })}
                rows={2}
                className={errors.evidenceNote ? 'border-red-500' : ''}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={formData.isActive} onCheckedChange={(v) => setFormData({ ...formData, isActive: v })} />
                <Label>Active</Label>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-4 border-t">
              <Button onClick={handleSave} disabled={isSaving} className="bg-[#1A6B3C] hover:bg-[#155a32]">
                {isSaving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                Save
              </Button>
              {editingAction && (
                <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)} disabled={isSaving}>
                  Delete
                </Button>
              )}
              <Button variant="outline" onClick={() => setPanelOpen(false)}>Cancel</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RescueActionsPage;
